import React, { useEffect, useState, useCallback } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faChartLine,
  faChartPie,
  faArrowUp,
  faArrowDown,
  faCheckCircle,
  faExclamationTriangle,
  faCircleInfo
} from "@fortawesome/free-solid-svg-icons";
import {
  faChartBar,
  faUser,
  faMoneyBill1
} from "@fortawesome/free-regular-svg-icons";

import toast from "react-hot-toast";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { getAdminDashboardSummary, getTransactionsByPlatform, getMyClientConfig } from "../../../lib/apiClient";
import { ROUTES } from "../../../lib/constants";
import { useNavigate } from "react-router-dom";
import { AdminDashboardSummaryResponse, TransactionAdminResponse } from "../../../types";
import Modal from "../../../components/ui/Modal";
import TransactionTable from "../../../components/admin/TransactionTable";
import { getErrorMessage } from "../../../lib/utils";
import Select from "../../../components/ui/Select";

const PIE_COLORS = [
  "#ffbc06",
  "#3b82f6",
  "#22c55e",
  "#ef4444",
  "#a855f7",
  "#f97316",
  "#6b7280",
];

const PLATFORM_COLORS: Record<string, string> = {
  telegram: "#0088cc",
  discord: "#5865F2",
  slack: "#36C5F0",
  manual_add: "#9ca3af",
};

const getPlatformKey = (platform?: string | null) => {
  const key = String(platform || "").toLowerCase().trim();
  return ["telegram", "discord", "slack"].includes(key) ? key : "manual_add";
};

const getPlatformLabel = (platform?: string | null) => {
  const key = getPlatformKey(platform);
  if (key === "manual_add") return "Manual Add";
  return `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
};

const StatCard: React.FC<{
  title: React.ReactNode;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
}> = ({ title, value, description, icon, trend }) => (
  <div className="group overflow-visible bg-gradient-to-br from-dark-secondary to-dark-tertiary p-5 rounded-xl shadow-lg border border-dark-border hover:border-golden-accent/30 transition-all duration-300 hover:shadow-xl hover:shadow-golden-accent/5 min-h-[100px]">
    <div className="flex items-center space-x-4 h-full">
      {icon && (
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-golden-accent rounded-lg flex items-center justify-center text-text-on-accent shadow-lg">
            {icon}
          </div>
        </div>
      )}
      
      <div className="flex-1 min-w-0 flex flex-col justify-center overflow-visible">
        <h3 className="text-sm font-medium text-text-secondary mb-2 font-montserrat overflow-visible">
          {title}
        </h3>
        
        <p className="text-2xl font-bold text-text-primary group-hover:text-golden-accent transition-colors duration-300 leading-tight font-montserrat mb-1">
          {value}
        </p>
        
        {trend && (
          <div className={`inline-flex items-center text-xs font-medium mt-1 ${
            trend.isPositive 
              ? 'text-functional-success' 
              : 'text-functional-danger'
          }`}>
            <FontAwesomeIcon 
              icon={trend.isPositive ? faArrowUp : faArrowDown} 
              className="mr-1 text-xs" 
            />
            {Math.abs(trend.value)}%
          </div>
        )}
        
        {description && (
          <p className="text-xs text-text-secondary mt-0.5 truncate">{description}</p>
        )}
      </div>
    </div>
  </div>
);

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AdminDashboardSummaryResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardingBanner, setOnboardingBanner] = useState<{ percentage: number } | null>(null);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const today = new Date();
  const last30DaysStart = new Date(today);
  last30DaysStart.setDate(last30DaysStart.getDate() - 29);

  const formatDefaultDate = (date: Date) => date.toISOString().split("T")[0];
  const getLastDayOfMonth = (year: number, month: number) => new Date(year, month, 0).getDate();
  const getEffectiveRange = (
    agg: "mon" | "yer" | "day" | "none",
    start: string,
    end: string,
    year: number,
    month: number
  ) => {
    if (agg === "mon") {
      const monthStart = `${year}-${String(month).padStart(2, "0")}-01`;
      const monthEnd = `${year}-${String(month).padStart(2, "0")}-${String(
        getLastDayOfMonth(year, month)
      ).padStart(2, "0")}`;
      return { start: monthStart, end: monthEnd };
    }

    if (agg === "yer") {
      return { start: `${year}-01-01`, end: `${year}-12-31` };
    }

    if (agg === "none") {
      return {
        start: formatDefaultDate(last30DaysStart),
        end: formatDefaultDate(today),
      };
    }

    return { start, end };
  };

  const [startDate, setStartDate] = useState<string>(
    formatDefaultDate(last30DaysStart)
  );
  const [endDate, setEndDate] = useState<string>(
    formatDefaultDate(today)
  );
  const [aggregation, setaggregation] = useState<"mon" | "yer" | "day" | "none">("none")
  const aggregationOptions = [
    { val: "none", lab: "Default (Last 30 Days)" },
    { val: "day", lab: "Custom Range" },
    { val: "mon", lab: "Monthly" },
    { val: "yer", lab: "Yearly" },
  ] as const;
  const monthOptions = Array.from({ length: new Date().getMonth() + 1 }, (_, i) => ({
    val: i + 1,
    lab: new Date(0, i).toLocaleString("default", { month: "short" }).toUpperCase(),
  }));
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => {
    const year = 2025 + i;
    return { val: year.toString(), lab: year.toString() };
  });
  const [selectedMon, setSelectedMon] = useState(today.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(today.getFullYear())
  const [activeRange, setActiveRange] = useState<{ start: string; end: string }>({
    start: formatDefaultDate(last30DaysStart),
    end: formatDefaultDate(today),
  });

  // Platform drill-down modal state
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [platformTransactions, setPlatformTransactions] = useState<TransactionAdminResponse[]>([]);
  const [isLoadingPlatformTransactions, setIsLoadingPlatformTransactions] = useState(false);

  // Helper to shift a date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) by a timezone offset in minutes
  // For dashboard filters, always treat as 18:30 UTC the previous day for start, and 18:30 UTC the selected day for end
  const shiftDateStringToUTC1830 = (dateStr: string, isEnd: boolean) => {
    // dateStr is always YYYY-MM-DD
    // For start: 18:30 previous day; for end: 18:30 selected day
    let baseDate = new Date(dateStr + 'T18:30:00Z');
    if (!isEnd) {
      // For start, subtract 1 day
      baseDate.setUTCDate(baseDate.getUTCDate() - 1);
    }
    return baseDate.toISOString().replace('.000Z', 'Z');
  };

  // For IST to UTC dashboard filter, use above logic
  const adjustToUTC = (dateStr: string, isEnd: boolean) => shiftDateStringToUTC1830(dateStr, isEnd);

  const fetchSummary = async (
    override?: {
      start?: string;
      end?: string;
      agg?: "mon" | "yer" | "day" | "none";
      year?: number;
      month?: number;
      skipCache?: boolean;
    }
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const effectiveStart = override?.start ?? startDate;
      const effectiveEnd = override?.end ?? endDate;
      const effectiveAgg = override?.agg ?? aggregation;
      const effectiveYear = override?.year ?? selectedYear;
      const effectiveMonth = override?.month ?? selectedMon;
      const effectiveRange = getEffectiveRange(
        effectiveAgg,
        effectiveStart,
        effectiveEnd,
        effectiveYear,
        effectiveMonth
      );

      if (!effectiveRange.start || !effectiveRange.end || new Date(effectiveRange.start) > new Date(effectiveRange.end)) {
        const msg = "Please select a valid date range.";
        setError(msg);
        toast.error(msg);
        setIsLoading(false);
        return;
      }
      // Adjust filter dates to UTC 18:30 logic
      const utcStart = adjustToUTC(effectiveRange.start, false); // false = start
      const utcEnd = adjustToUTC(effectiveRange.end, true); // true = end

      console.log(
        `Fetching summary for date range: ${utcStart} to ${utcEnd} ${effectiveAgg} ${effectiveYear} ${effectiveMonth}`
      );

      const data = await getAdminDashboardSummary(utcStart, utcEnd, effectiveAgg, effectiveYear, effectiveMonth, override?.skipCache ?? false);
      setSummary(data);
      setActiveRange(
        data.appliedRange
          ? { start: data.appliedRange.startDate, end: data.appliedRange.endDate }
          : { start: utcStart, end: utcEnd }
      );
      if (
        data &&
        (data.dailyRevenue.length === 0 || data.revenueByChannel.length === 0)
      ) {
        toast(
          (t) => (
            <span className="flex items-center gap-2">
              <span role="img" aria-label="chart">📊</span>
              No data available for the selected date range.
              <button
                className="ml-4 px-2 py-1 rounded bg-dark-tertiary hover:bg-dark-border text-xs text-text-secondary hover:text-text-primary transition-colors"
                onClick={() => toast.dismiss(t.id)}
                aria-label="Close notification"
              >
                ✕
              </button>
            </span>
          ),
          { id: "no-data-toast" }
        );
      }
    } catch (err) {
      const message = getErrorMessage(err);
      setError(`Failed to load dashboard summary: ${message}`);
      toast.error(`Error loading summary: ${message}`, {
        id: "admin-summary-error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    getMyClientConfig().then((cfg) => {
      if (cfg.onboarding_status !== "active" || (cfg.completion_percentage ?? 100) < 100) {
        setOnboardingBanner({ percentage: cfg.completion_percentage ?? 0 });
      }
    }).catch(() => { /* silently ignore */ });
  }, [aggregation, selectedMon, selectedYear]);

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  // Quick range helpers (Today / Week / Month / Quarter)
  const format = (d: Date) => d.toISOString().split("T")[0];
  const setQuickRange = (range: "today" | "week" | "month" | "quarter") => {
    const now = new Date();
    let s = new Date(now);
    let e = new Date(now);
    if (range === "today") {
      // s and e already set to today
    } else if (range === "week") {
      s.setDate(now.getDate() - 6);
    } else if (range === "month") {
      s = new Date(now.getFullYear(), now.getMonth(), 1);
      e = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (range === "quarter") {
      const q = Math.floor(now.getMonth() / 3);
      s = new Date(now.getFullYear(), q * 3, 1);
      e = new Date(now.getFullYear(), q * 3 + 3, 0);
    }
    const sStr = format(s);
    const eStr = format(e);
    setStartDate(sStr);
    setEndDate(eStr);
    setaggregation("day");
    fetchSummary({ start: sStr, end: eStr, agg: "day", skipCache: true });
  };

  // Platform drill-down handlers
  const handlePlatformClick = async (platform: string) => {
    const platformKey = getPlatformKey(platform);
    setSelectedPlatform(platformKey);
    setIsPlatformModalOpen(true);
    setIsLoadingPlatformTransactions(true);

    try {
      const result = await getTransactionsByPlatform(platformKey, activeRange.start, activeRange.end);
      setPlatformTransactions(result.transactions);
    } catch (err) {
      toast.error(`Error loading ${getPlatformLabel(platform)} transactions`);
      setPlatformTransactions([]);
    } finally {
      setIsLoadingPlatformTransactions(false);
    }
  };

  const closePlatformModal = () => {
    setIsPlatformModalOpen(false);
    setSelectedPlatform(null);
    setPlatformTransactions([]);
  };

  const channelRevenue = summary?.channelRevenue ?? summary?.revenueBySource?.find((entry) => entry.source === "channel")?.totalRevenue ?? 0;
  const comboRevenue = summary?.comboRevenue ?? summary?.revenueBySource?.find((entry) => entry.source === "combo")?.totalRevenue ?? 0;
  const comboTransactionCount = summary?.revenueBySource?.find((entry) => entry.source === "combo")?.count ?? 0;

  // Calculate platform revenue verification
  const getPlatformRevenueVerification = () => {
    if (!summary?.revenueByPlatform || summary.revenueByPlatform.length === 0) {
      return { isVerified: true, discrepancy: 0, platformSum: 0 };
    }
    const platformSum = summary.revenueByPlatform.reduce((acc, p) => acc + p.totalRevenue, 0);
    const verificationBase = summary.channelRevenue ?? summary.totalRevenue ?? 0;
    const discrepancy = Math.abs(verificationBase - platformSum);
    const isVerified = discrepancy < 0.01;
    return { isVerified, discrepancy, platformSum };
  };

  const renderDashboardContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-10">
          <LoadingIndicator size="lg" text="Loading dashboard data..." />
        </div>
      );
    }
    if (error || !summary) {
      return (
        <div className="py-10">
          <ErrorMessage
            title="Error Loading Dashboard"
            message={error || "Could not fetch summary."}
          />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Enhanced Stat Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-stretch">
          <StatCard
            title="Total Revenue"
            value={`₹${summary.totalRevenue?.toFixed(0) ?? "N/A"}`}
            description={`Channels ₹${channelRevenue.toFixed(0)} | Combos ₹${comboRevenue.toFixed(0)}`}
            icon={<FontAwesomeIcon icon={faMoneyBill1} className="text-xl" />}
          />
          <StatCard
            title="Combo Revenue"
            value={`₹${comboRevenue.toFixed(0)}`}
            description={comboTransactionCount > 0 ? `${comboTransactionCount} combo transactions` : "No combo transactions"}
            icon={<FontAwesomeIcon icon={faMoneyBill1} className="text-xl" />}
          />
          <StatCard
            title="Total Renewals"
            value={summary.totalRenewals ?? "N/A"}
            icon={<FontAwesomeIcon icon={faSync} className="text-xl" />}
          />
          <StatCard
            title={
              <span className="inline-flex items-center gap-1.5">
                <span>Churn Rate</span>
                <span className="relative inline-flex group/churn-tooltip">
                  <button
                    type="button"
                    tabIndex={0}
                    aria-label="Churn rate formula"
                    className="inline-flex items-center justify-center text-[11px] text-text-secondary/90 hover:text-golden-accent focus:text-golden-accent transition-colors"
                  >
                    <FontAwesomeIcon icon={faCircleInfo} className="text-[11px]" />
                  </button>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-20 w-64 -translate-x-1/2 rounded-lg border border-dark-border bg-[#111111]/95 px-3 py-2 text-[11px] font-normal leading-relaxed text-text-secondary opacity-0 shadow-xl backdrop-blur-sm transition-opacity duration-150 group-hover/churn-tooltip:opacity-100 group-focus-within/churn-tooltip:opacity-100"
                  >
                    Churn % = (Expired + Revoked in selected range) / (Subscribers active at range start; fallback: subscribers active anytime in range) x 100.
                  </span>
                </span>
              </span>
            }
            value={
              summary.churnRate != null
                ? `${summary.churnRate.toFixed(2)}%`
                : "N/A"
            }
            icon={<FontAwesomeIcon icon={faArrowDown} className="text-xl" />}
          />
          <StatCard
            title="Avg Lifetime Value"
            value={`₹${summary.avgLifetimeValue?.toFixed(0) ?? "N/A"}`}
            icon={<FontAwesomeIcon icon={faChartLine} className="text-xl" />}
          />
          <StatCard
            title="Active Subscribers"
            value={summary.totalSubscribers ?? "N/A"}
            icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-dark-secondary to-dark-tertiary p-8 rounded-2xl shadow-xl border border-dark-border hover:border-golden-accent/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary flex items-center font-montserrat">
                <FontAwesomeIcon icon={faChartPie} className="mr-3 text-xl" />
                Revenue by Channel
              </h3>
              <div className="px-3 py-1 bg-golden-accent/10 rounded-full text-xs font-medium text-golden-accent">
                Active Channels
              </div>
            </div>
            {summary.revenueByChannel.length > 0 ? (
              <div className="w-full h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.revenueByChannel}
                      dataKey="totalRevenue"
                      nameKey="channelName"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                    // label={({ channelName, percent }) =>
                    //   `${channelName} (${(percent * 100).toFixed(0)}%)`
                    // }
                    >
                      {summary.revenueByChannel.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => `₹${value.toFixed(0)}`}
                    />

                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-text-secondary text-sm py-10 text-center">
                No channel revenue data available for this period.
              </p>
            )}
          </div>

          <div className="bg-gradient-to-br from-dark-secondary to-dark-tertiary p-8 rounded-2xl shadow-xl border border-dark-border hover:border-golden-accent/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary flex items-center font-montserrat">
                <FontAwesomeIcon icon={faChartPie} className="mr-3 text-xl" />
                Channel Revenue by Platform
              </h3>
              <div className="flex items-center gap-2">
                <div className="px-3 py-1 bg-blue-500/10 rounded-full text-xs font-medium text-blue-400">
                  By Platform
                </div>
                {summary.revenueByPlatform && summary.revenueByPlatform.length > 0 && (() => {
                  const { isVerified, discrepancy } = getPlatformRevenueVerification();
                  return (
                    <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                      isVerified
                        ? 'bg-functional-success/10 text-functional-success'
                        : 'bg-functional-warning/10 text-functional-warning'
                    }`}>
                      <FontAwesomeIcon
                        icon={isVerified ? faCheckCircle : faExclamationTriangle}
                        className="text-xs"
                      />
                      {isVerified
                        ? 'Verified'
                        : `Gap: ₹${discrepancy.toFixed(0)}`
                      }
                    </div>
                  );
                })()}
              </div>
            </div>
            {summary.revenueByPlatform && summary.revenueByPlatform.length > 0 ? (
              <>
                <div className="w-full h-64 md:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.revenueByPlatform}
                        dataKey="totalRevenue"
                        nameKey="platform"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        onClick={(data) => handlePlatformClick(data.platform)}
                        style={{ cursor: 'pointer' }}
                      >
                        {summary.revenueByPlatform.map((entry, index) => {
                          const color = PLATFORM_COLORS[getPlatformKey(entry.platform)] || "#9ca3af";
                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={color}
                            />
                          );
                        })}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string, props: { payload?: { platform?: string; count?: number } }) => {
                          const activePlatformKey = getPlatformKey(props.payload?.platform);
                          const platformData = summary.revenueByPlatform?.find(p => getPlatformKey(p.platform) === activePlatformKey);
                          const totalPlatformRevenue = summary.revenueByPlatform?.reduce((acc, p) => acc + p.totalRevenue, 0) || 0;
                          const percentage = totalPlatformRevenue > 0 ? ((value / totalPlatformRevenue) * 100).toFixed(0) : '0';
                          return [`₹${value.toFixed(0)} (${percentage}%) - ${platformData?.count || 0} txns`, ''];
                        }}
                        labelFormatter={(label) => {
                          return getPlatformLabel(label);
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Custom Legend with Stats */}
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {summary.revenueByPlatform.map((entry) => {
                    const totalPlatformRevenue = summary.revenueByPlatform?.reduce((acc, p) => acc + p.totalRevenue, 0) || 0;
                    const percentage = totalPlatformRevenue > 0 ? ((entry.totalRevenue / totalPlatformRevenue) * 100).toFixed(0) : '0';
                    const platformKey = getPlatformKey(entry.platform);
                    const color = PLATFORM_COLORS[platformKey] || "#9ca3af";
                    return (
                      <button
                        key={entry.platform}
                        onClick={() => handlePlatformClick(platformKey)}
                        className="flex items-center justify-between p-3 bg-dark-tertiary/50 rounded-lg hover:bg-dark-tertiary transition-colors cursor-pointer text-left"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-text-primary font-medium">
                            {getPlatformLabel(entry.platform)}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-text-primary font-semibold">₹{entry.totalRevenue.toFixed(0)}</div>
                          <div className="text-text-secondary text-xs">{percentage}% - {entry.count} txns</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-text-secondary text-xs mt-3 text-center">Platform chart excludes combo transactions. Click on chart or legend to view channel transactions.</p>
              </>
            ) : (
              <p className="text-text-secondary text-sm py-10 text-center">
                No platform revenue data available for this period.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 mt-8">
          <div className="bg-gradient-to-br from-dark-secondary to-dark-tertiary p-8 rounded-2xl shadow-xl border border-dark-border hover:border-golden-accent/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-text-primary flex items-center font-montserrat">
                <FontAwesomeIcon icon={faChartBar} className="mr-3 text-xl" />
                {aggregationOptions.find((ag) => ag.val === aggregation)?.lab} Revenue
              </h3>
              <div className="px-3 py-1 bg-functional-success/10 rounded-full text-xs font-medium text-functional-success">
                Trending Up
              </div>
            </div>
            {summary.dailyRevenue.length > 0 ? (
              <div className="w-full h-80 md:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={summary.dailyRevenue}

                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis dataKey="_id" stroke="#999" />
                    <YAxis stroke="#999" />
                    <Tooltip
                      cursor={false}
                      content={({ label, payload }) => {
                        const value = payload?.[0]?.value;
                        return (
                          <div className="p-2 bg-white rounded shadow text-sm text-gray-800">
                            <p><strong>{aggregation === "yer" ? "Month" : "Date"}:</strong> {label}</p>
                            <p><strong>Value:</strong> ₹{value?.toFixed(0)}</p>
                          </div>
                        );
                      }}
                      formatter={(value: number) => `₹${value.toFixed(0)}`}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#ffbc06"
                      barSize={30}
                      isAnimationActive={false}

                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-text-secondary text-sm py-10 text-center">
                No daily revenue data available for this period.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Outer spacing */}
      <PageTitle
        title="Admin Dashboard"
        subtitle="Overview of key channel metrics and performance."
      />
      {/* Onboarding Incomplete Banner */}
      {onboardingBanner && !isBannerDismissed && (
        <div className="flex items-center gap-4 bg-functional-warning/10 border border-functional-warning/30 rounded-xl px-5 py-4">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-functional-warning text-xl flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-text-primary font-semibold text-sm font-montserrat">
              Account Setup Incomplete
            </p>
            <p className="text-text-secondary text-xs mt-0.5">
              Your configuration is {onboardingBanner.percentage}% complete. Finish setup to unlock all platform features.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(ROUTES.ADMIN_SETTINGS)}
          >
            Complete Setup
          </Button>
          <button
            type="button"
            aria-label="Dismiss"
            className="text-text-secondary hover:text-text-primary transition-colors text-lg leading-none ml-1"
            onClick={() => setIsBannerDismissed(true)}
          >
            &times;
          </button>
        </div>
      )}
      {/* Refined subtitle */}
      {/* Main content container */}
      <div className="bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary p-8 md:p-10 rounded-2xl shadow-2xl border border-dark-border backdrop-blur-sm">
        {/* Analytics By - Modern Tabbed control */}
        <div className="mb-8">
          <div className="w-full">
            <div className="flex flex-wrap items-center justify-center sm:justify-start p-1 bg-dark-tertiary rounded-xl border border-dark-border shadow-inner w-full min-w-[220px] sm:min-w-0">
              {aggregationOptions.map((opt, index) => {
                const active = aggregation === opt.val;
                let shortLabel = '';
                if (opt.val === 'none') shortLabel = 'Default';
                else if (opt.val === 'day') shortLabel = 'Range';
                else if (opt.val === 'mon') shortLabel = 'Month';
                else if (opt.val === 'yer') shortLabel = 'Year';
                return (
                  <button
                    key={opt.val}
                    type="button"
                    onClick={() => setaggregation(opt.val)}
                    className={`relative px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 ease-out ${
                      active
                        ? "text-black bg-golden-accent transform scale-105"
                        : "text-text-secondary hover:text-text-primary hover:bg-dark-secondary/50"
                    } ${index === 0 ? "rounded-l-lg" : ""} ${index === aggregationOptions.length - 1 ? "rounded-r-lg" : ""}`}
                  >
                    <span className="inline sm:hidden">{shortLabel}</span>
                    <span className="hidden sm:inline">{opt.lab}</span>
                    {active && (
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-golden-accent/20 to-golden-accent/10 animate-pulse"></div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* Date Range Selector */}
        {aggregation === "day" ?

          <div className="flex flex-wrap items-center gap-4 mb-6 border-b border-dark-tertiary pb-6">
            {/* Added bottom border and padding */}
            {/* Using Input component for date pickers */}
            <Input
              label="Start Date"
              id="start-date"
              name="start-date"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              containerClassName="mb-0"
              disabled={isLoading}
            />
            <Input
              label="End Date"
              id="end-date"
              name="end-date"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              containerClassName="mb-0"
              disabled={isLoading}
            />
            {/* Apply Button */}
            <Button onClick={() => fetchSummary({ skipCache: true })} disabled={isLoading}>
              Apply Filter
            </Button>
            {/* Disabled while loading */}
          </div> :
          aggregation === "none" ?
            <></> :
            aggregation === "mon" ?
              <>
                <Select
                  id="selectedMonth"
                  name="selectedMonth"
                  label="Month"
                  placeholder="Select Month"
                  value={selectedMon.toString() || "1"}
                  onChange={(e) => setSelectedMon(Number(e.target.value))}
                  options={monthOptions.map((aggre) => ({
                    value: aggre.val,
                    label: aggre.lab,
                  }))}
                  className="bg-dark-secondary text-text-primary"
                  containerClassName=""
                />
                <Select
                  id="selectedYear"
                  name="selectedYear"
                  label="Year"
                  placeholder="Select Year"
                  value={selectedYear.toString()}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  options={yearOptions.map((aggre) => ({
                    value: aggre.val,
                    label: aggre.lab,
                  }))}
                  className="bg-dark-secondary text-text-primary"
                  containerClassName=""
                />
              </>
              :
              <Select
                id="selectedYear"
                name="selectedYear"
                label="Year"
                placeholder="Select Year"
                value={selectedYear.toString()}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                options={yearOptions.map((aggre) => ({
                  value: aggre.val,
                  label: aggre.lab,
                }))}
                className="bg-dark-secondary text-text-primary"
                containerClassName=""
              />
        }

        {/* Render Dashboard Content (Stats, Charts, Errors, Loading) */}
        {renderDashboardContent()}
      </div>

      {/* Platform Transactions Drill-down Modal */}
      <Modal
        isOpen={isPlatformModalOpen}
        onClose={closePlatformModal}
        title={`${getPlatformLabel(selectedPlatform)} Transactions`}
        size="3xl"
      >
        <div className="space-y-4">
          {/* Summary stats */}
          {selectedPlatform && summary?.revenueByPlatform && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-dark-tertiary p-4 rounded-lg">
                <p className="text-text-secondary text-sm">Total Revenue</p>
                <p className="text-text-primary text-xl font-bold">
                  ₹{summary.revenueByPlatform.find(p => getPlatformKey(p.platform) === selectedPlatform)?.totalRevenue.toFixed(0) || '0'}
                </p>
              </div>
              <div className="bg-dark-tertiary p-4 rounded-lg">
                <p className="text-text-secondary text-sm">Transaction Count</p>
                <p className="text-text-primary text-xl font-bold">
                  {summary.revenueByPlatform.find(p => getPlatformKey(p.platform) === selectedPlatform)?.count || 0}
                </p>
              </div>
            </div>
          )}

          {/* Transaction Table */}
          <TransactionTable
            transactions={platformTransactions}
            isLoading={isLoadingPlatformTransactions}
          />

          {!isLoadingPlatformTransactions && platformTransactions.length === 0 && (
            <p className="text-text-secondary text-center py-4">
              No transactions found for this platform in the selected date range.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}
